// Normalize acquisition/customer record text fields to uppercase as users type.
// Intentionally excludes free-form communication, notes, email, passwords, URLs,
// search boxes, dates/times and other fields where forced uppercase would be harmful.

const UPPERCASE_HINTS = [
  'name','first name','last name','customer name',
  'address','street','city','state','zip','postal',
  'vin','vehicle','year','make','model','trim',
  'stock','license plate','plate','assigned to','appraiser'
];

const EXCLUDED_HINTS = [
  'email','phone','message','conversation','note','notes','comment','search',
  'password','url','website','next action','sms','text message'
];

const descriptor = el => [
  el.name,
  el.id,
  el.placeholder,
  el.getAttribute('aria-label'),
  el.getAttribute('data-field'),
  el.closest('label')?.innerText
].filter(Boolean).join(' ').toLowerCase();

const shouldUppercase = el => {
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return false;
  if (el.dataset.vcUppercase === 'off') return false;
  const type = String(el.type || 'text').toLowerCase();
  if (['email','password','tel','number','date','time','datetime-local','url','search'].includes(type)) return false;
  const hint = descriptor(el);
  if (EXCLUDED_HINTS.some(word => hint.includes(word))) return false;
  return UPPERCASE_HINTS.some(word => hint.includes(word));
};

const normalize = el => {
  if (!shouldUppercase(el) || !el.value) return;
  const upper = el.value.toUpperCase();
  if (upper === el.value) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  el.value = upper;
  // React tracks native input values. Dispatching input keeps controlled state in sync.
  el.dispatchEvent(new Event('input', { bubbles: true }));
  try { if (start !== null && end !== null) el.setSelectionRange(start, end); } catch {}
};

const decorate = root => {
  const fields = root?.matches?.('input,textarea') ? [root] : [...(root?.querySelectorAll?.('input,textarea') || [])];
  fields.forEach(el => {
    if (!shouldUppercase(el)) return;
    el.style.textTransform = 'uppercase';
    el.setAttribute('autocapitalize', 'characters');
    el.setAttribute('spellcheck', 'false');
    // Normalize an existing editable value too, so pasted/lowercase data is consistent.
    if (document.activeElement !== el) normalize(el);
  });
};

const handleInput = event => normalize(event.target);
const handlePaste = event => {
  if (!shouldUppercase(event.target)) return;
  requestAnimationFrame(() => normalize(event.target));
};

document.addEventListener('input', handleInput, true);
document.addEventListener('change', handleInput, true);
document.addEventListener('paste', handlePaste, true);

const observer = new MutationObserver(records => {
  records.forEach(record => record.addedNodes.forEach(node => {
    if (node.nodeType === 1) decorate(node);
  }));
});

const start = () => {
  decorate(document);
  observer.observe(document.body, { childList: true, subtree: true });
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();
